import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Plus, Trash2, Receipt, CheckCircle2, RotateCcw, FileText, Search, Image as ImageIcon, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { MediaPickerDialog } from "@/components/admin/media-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  upsertInvoice, markInvoicePaid, refundInvoice, setInvoiceStatus, saveInvoiceBranding,
} from "@/lib/invoices.functions";

export const Route = createFileRoute("/_authenticated/admin/pos")({
  component: PosPage,
});

type LineItem = {
  description: string;
  qty: number;
  unit_price_cents: number;
  issue_license: boolean;
  product_id?: string | null;
};

type Invoice = {
  id: string;
  number: string;
  status: string;
  recurrence: string;
  customer_name: string | null;
  customer_email: string | null;
  user_id: string | null;
  total_cents: number;
  amount_paid_cents: number;
  amount_refunded_cents: number;
  currency: string;
  issue_date: string;
  due_date: string | null;
  created_at: string;
};

function fmt(c: number, ccy = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(c / 100);
}

const statusColor: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-blue-500/15 text-blue-500",
  paid: "bg-emerald-500/15 text-emerald-500",
  overdue: "bg-amber-500/15 text-amber-500",
  void: "bg-zinc-500/15 text-zinc-500",
  refunded: "bg-rose-500/15 text-rose-500",
  partially_refunded: "bg-orange-500/15 text-orange-500",
};

function PosPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState<Invoice | null>(null);

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["admin", "invoices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data as Invoice[];
    },
  });

  const filtered = useMemo(() => {
    return invoices.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          i.number.toLowerCase().includes(q) ||
          (i.customer_name ?? "").toLowerCase().includes(q) ||
          (i.customer_email ?? "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [invoices, search, statusFilter]);

  const totals = useMemo(() => {
    const paid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total_cents, 0);
    const outstanding = invoices
      .filter((i) => ["sent", "overdue", "partially_refunded"].includes(i.status))
      .reduce((s, i) => s + (i.total_cents - i.amount_paid_cents), 0);
    const refunded = invoices.reduce((s, i) => s + i.amount_refunded_cents, 0);
    return { paid, outstanding, refunded, count: invoices.length };
  }, [invoices]);

  const markPaid = useServerFn(markInvoicePaid);
  const setStatus = useServerFn(setInvoiceStatus);

  const onMarkPaid = async (id: string) => {
    try {
      await markPaid({ data: { invoice_id: id } });
      toast.success("Invoice marked as paid");
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    }
  };

  const onSetStatus = async (id: string, status: "draft" | "sent" | "void") => {
    try {
      await setStatus({ data: { invoice_id: id, status } });
      toast.success(`Marked as ${status}`);
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">POS & Invoicing</h1>
          <p className="text-sm text-muted-foreground">
            Create invoices, take payments, refund, and auto-issue licenses.
          </p>
        </div>
        <Button onClick={() => setEditorOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> New invoice
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Invoices" value={String(totals.count)} icon={<Receipt className="h-4 w-4" />} />
        <StatCard label="Collected" value={fmt(totals.paid)} icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />} />
        <StatCard label="Outstanding" value={fmt(totals.outstanding)} icon={<FileText className="h-4 w-4 text-amber-500" />} />
        <StatCard label="Refunded" value={fmt(totals.refunded)} icon={<RotateCcw className="h-4 w-4 text-rose-500" />} />
      </div>

      <Tabs defaultValue="invoices">
        <PillTabsList search>
          <PillTabsTrigger value="invoices">Invoices</PillTabsTrigger>
          <PillTabsTrigger value="credit-notes">Credit notes</PillTabsTrigger>
          <PillTabsTrigger value="branding">Branding</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="invoices" className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search number, customer, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="void">Void</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
                <SelectItem value="partially_refunded">Partially refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-border bg-card">
            <div className="hidden grid-cols-12 gap-3 border-b border-border px-4 py-2 text-xs font-medium text-muted-foreground md:grid">
              <div className="col-span-2">Number</div>
              <div className="col-span-3">Customer</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Cycle</div>
              <div className="col-span-2">Total</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {isLoading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No invoices yet.</div>
            ) : (
              filtered.map((inv) => (
                <div key={inv.id} className="grid grid-cols-1 gap-2 border-b border-border px-4 py-3 text-sm last:border-0 md:grid-cols-12 md:items-center">
                  <div className="col-span-2 font-mono text-xs">{inv.number}</div>
                  <div className="col-span-3">
                    <div className="font-medium">{inv.customer_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{inv.customer_email ?? "—"}</div>
                  </div>
                  <div className="col-span-2">
                    <Badge className={statusColor[inv.status] ?? "bg-muted"}>{inv.status}</Badge>
                  </div>
                  <div className="col-span-1 text-xs capitalize text-muted-foreground">{inv.recurrence.replace("_", " ")}</div>
                  <div className="col-span-2">
                    <div className="font-semibold">{fmt(inv.total_cents, inv.currency)}</div>
                    {inv.amount_refunded_cents > 0 && (
                      <div className="text-xs text-rose-500">−{fmt(inv.amount_refunded_cents, inv.currency)} refunded</div>
                    )}
                  </div>
                  <div className="col-span-2 flex flex-wrap justify-end gap-1">
                    {inv.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => onSetStatus(inv.id, "sent")}>Send</Button>
                    )}
                    {inv.status !== "paid" && inv.status !== "void" && inv.status !== "refunded" && (
                      <Button size="sm" onClick={() => onMarkPaid(inv.id)}>Paid</Button>
                    )}
                    {inv.status === "paid" || inv.status === "partially_refunded" ? (
                      <Button size="sm" variant="outline" onClick={() => setRefundOpen(inv)}>Refund</Button>
                    ) : null}
                    {inv.status !== "paid" && inv.status !== "void" && (
                      <Button size="sm" variant="ghost" onClick={() => onSetStatus(inv.id, "void")}>Void</Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="credit-notes">
          <CreditNotesTable />
        </TabsContent>

        <TabsContent value="branding">
          <BrandingEditor />
        </TabsContent>
      </Tabs>

      <InvoiceEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={() => qc.invalidateQueries({ queryKey: ["admin", "invoices"] })}
      />
      {refundOpen && (
        <RefundDialog
          invoice={refundOpen}
          onClose={() => setRefundOpen(null)}
          onDone={() => {
            setRefundOpen(null);
            qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{label}</span>{icon}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function InvoiceEditor({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const save = useServerFn(upsertInvoice);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [recurrence, setRecurrence] = useState<"one_time" | "monthly" | "yearly" | "lifetime">("one_time");
  const [currency, setCurrency] = useState("USD");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [taxCents, setTaxCents] = useState(0);
  const [discountCents, setDiscountCents] = useState(0);
  const [items, setItems] = useState<LineItem[]>([
    { description: "", qty: 1, unit_price_cents: 0, issue_license: false },
  ]);

  const subtotal = items.reduce((s, i) => s + i.qty * i.unit_price_cents, 0);
  const total = Math.max(0, subtotal + taxCents - discountCents);

  const reset = () => {
    setCustomerName(""); setCustomerEmail(""); setUserId("");
    setRecurrence("one_time"); setCurrency("USD"); setDueDate(""); setNotes("");
    setTaxCents(0); setDiscountCents(0);
    setItems([{ description: "", qty: 1, unit_price_cents: 0, issue_license: false }]);
  };

  const onSubmit = async () => {
    const cleanItems = items.filter((i) => i.description.trim() && i.qty > 0);
    if (!cleanItems.length) {
      toast.error("Add at least one line item");
      return;
    }
    try {
      await save({
        data: {
          user_id: userId.trim() || null,
          customer_name: customerName || null,
          customer_email: customerEmail || null,
          recurrence,
          currency,
          tax_cents: taxCents,
          discount_cents: discountCents,
          due_date: dueDate || null,
          notes: notes || null,
          items: cleanItems,
        },
      });
      toast.success("Invoice created");
      reset();
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Customer name</Label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} /></div>
            <div><Label>Customer email</Label><Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} /></div>
            <div><Label>Link to user ID (optional)</Label><Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="uuid" /></div>
            <div>
              <Label>Billing</Label>
              <Select value={recurrence} onValueChange={(v) => setRecurrence(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="lifetime">Lifetime</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Currency</Label><Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} /></div>
            <div><Label>Due date</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
          </div>

          <div className="rounded-lg border border-border">
            <div className="border-b border-border px-3 py-2 text-sm font-semibold">Line items</div>
            <div className="divide-y divide-border">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 p-3">
                  <Input className="col-span-5" placeholder="Description" value={it.description} onChange={(e) => {
                    const copy = [...items]; copy[idx] = { ...it, description: e.target.value }; setItems(copy);
                  }} />
                  <Input className="col-span-2" type="number" min={1} placeholder="Qty" value={it.qty} onChange={(e) => {
                    const copy = [...items]; copy[idx] = { ...it, qty: Math.max(1, parseInt(e.target.value || "1")) }; setItems(copy);
                  }} />
                  <Input className="col-span-3" type="number" min={0} step="0.01" placeholder="Unit price" value={it.unit_price_cents / 100} onChange={(e) => {
                    const copy = [...items]; copy[idx] = { ...it, unit_price_cents: Math.round(parseFloat(e.target.value || "0") * 100) }; setItems(copy);
                  }} />
                  <div className="col-span-1 flex items-center justify-center">
                    <Switch checked={it.issue_license} onCheckedChange={(v) => {
                      const copy = [...items]; copy[idx] = { ...it, issue_license: v }; setItems(copy);
                    }} title="Issue license on payment" />
                  </div>
                  <Button variant="ghost" size="icon" className="col-span-1" onClick={() => setItems(items.filter((_, i) => i !== idx))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="border-t border-border px-3 py-2">
              <Button variant="outline" size="sm" onClick={() => setItems([...items, { description: "", qty: 1, unit_price_cents: 0, issue_license: false }])}>
                <Plus className="mr-1 h-4 w-4" /> Add item
              </Button>
              <span className="ml-3 text-xs text-muted-foreground">Toggle the switch to auto-issue a license key on payment.</span>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div><Label>Tax (currency units)</Label><Input type="number" min={0} step="0.01" value={taxCents / 100} onChange={(e) => setTaxCents(Math.round(parseFloat(e.target.value || "0") * 100))} /></div>
            <div><Label>Discount</Label><Input type="number" min={0} step="0.01" value={discountCents / 100} onChange={(e) => setDiscountCents(Math.round(parseFloat(e.target.value || "0") * 100))} /></div>
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="flex justify-between text-xs"><span>Subtotal</span><span>{fmt(subtotal, currency)}</span></div>
              <div className="flex justify-between text-xs"><span>Tax</span><span>{fmt(taxCents, currency)}</span></div>
              <div className="flex justify-between text-xs"><span>Discount</span><span>−{fmt(discountCents, currency)}</span></div>
              <div className="mt-1 flex justify-between border-t border-border pt-1 text-sm font-semibold"><span>Total</span><span>{fmt(total, currency)}</span></div>
            </div>
          </div>

          <div><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit}>Create invoice</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RefundDialog({ invoice, onClose, onDone }: { invoice: Invoice; onClose: () => void; onDone: () => void }) {
  const refund = useServerFn(refundInvoice);
  const remaining = invoice.total_cents - invoice.amount_refunded_cents;
  const [amount, setAmount] = useState(remaining / 100);
  const [reason, setReason] = useState("");
  const [toWallet, setToWallet] = useState(!!invoice.user_id);

  const onSubmit = async () => {
    try {
      await refund({
        data: {
          invoice_id: invoice.id,
          amount_cents: Math.round(amount * 100),
          reason: reason || undefined,
          to_wallet: toWallet && !!invoice.user_id,
        },
      });
      toast.success("Refund issued");
      onDone();
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Refund {invoice.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">Remaining: {fmt(remaining, invoice.currency)}</div>
          <div><Label>Amount</Label><Input type="number" min={0} max={remaining / 100} step="0.01" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value || "0"))} /></div>
          <div><Label>Reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} /></div>
          {invoice.user_id && (
            <div className="flex items-center gap-2">
              <Switch checked={toWallet} onCheckedChange={setToWallet} />
              <span className="text-sm">Credit refund to user wallet</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit}>Issue refund</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreditNotesTable() {
  const { data = [], isLoading } = useQuery({
    queryKey: ["admin", "credit_notes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("credit_notes")
        .select("*, invoice:invoices(number, customer_name)")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="hidden grid-cols-12 gap-3 border-b border-border px-4 py-2 text-xs font-medium text-muted-foreground md:grid">
        <div className="col-span-2">Number</div>
        <div className="col-span-3">Invoice</div>
        <div className="col-span-3">Reason</div>
        <div className="col-span-2">Amount</div>
        <div className="col-span-2">Date</div>
      </div>
      {isLoading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : data.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">No credit notes yet.</div>
      ) : (
        data.map((cn) => (
          <div key={cn.id} className="grid grid-cols-1 gap-2 border-b border-border px-4 py-3 text-sm last:border-0 md:grid-cols-12">
            <div className="col-span-2 font-mono text-xs">{cn.number}</div>
            <div className="col-span-3">
              <div className="font-medium">{cn.invoice?.number}</div>
              <div className="text-xs text-muted-foreground">{cn.invoice?.customer_name ?? "—"}</div>
            </div>
            <div className="col-span-3 text-xs text-muted-foreground">{cn.reason ?? "—"}</div>
            <div className="col-span-2 font-semibold text-rose-500">−{fmt(cn.amount_cents, cn.currency)}</div>
            <div className="col-span-2 text-xs text-muted-foreground">{new Date(cn.created_at).toLocaleDateString()}</div>
          </div>
        ))
      )}
    </div>
  );
}

type Branding = {
  company_name: string;
  logo_url: string | null;
  phone: string;
  email: string;
  address: string;
  vat_number: string;
  website: string;
  footer_note: string;
  accent_color: string;
  show_logo: boolean;
};

const defaultBranding: Branding = {
  company_name: "",
  logo_url: null,
  phone: "",
  email: "",
  address: "",
  vat_number: "",
  website: "",
  footer_note: "Thank you for your business.",
  accent_color: "#4378BC",
  show_logo: true,
};

function BrandingEditor() {
  const qc = useQueryClient();
  const save = useServerFn(saveInvoiceBranding);
  const [b, setB] = useState<Branding>(defaultBranding);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["site_settings", "invoice_branding"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "invoice_branding")
        .maybeSingle();
      if (error) throw error;
      return (data?.value as Branding) ?? null;
    },
  });

  // Hydrate form when data loads
  useEffect(() => {
    if (data) setB({ ...defaultBranding, ...data });
  }, [data]);

  const onSave = async () => {
    setSaving(true);
    try {
      await save({ data: b });
      toast.success("Branding saved");
      qc.invalidateQueries({ queryKey: ["site_settings", "invoice_branding"] });
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Loading branding…</div>;
  }

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center space-y-3">
        <div className="text-sm font-medium text-destructive">Couldn't load branding settings</div>
        <div className="text-xs text-muted-foreground">{(error as Error).message}</div>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Editor */}
      <div className="space-y-4 rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold">Company details</h3>
            <p className="text-xs text-muted-foreground">Appears on every invoice header.</p>
          </div>
          <Button onClick={onSave} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>

        <div className="space-y-2">
          <Label>Company logo</Label>
          <div className="flex items-center gap-3">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/30">
              {b.logo_url ? (
                <img src={b.logo_url} alt="Logo" className="h-full w-full object-contain" />
              ) : (
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
              {b.logo_url ? "Replace" : "Upload"} logo
            </Button>
            {b.logo_url && (
              <Button variant="ghost" size="sm" onClick={() => setB({ ...b, logo_url: null })}>
                Remove
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Switch checked={b.show_logo} onCheckedChange={(v) => setB({ ...b, show_logo: v })} />
              <span className="text-xs text-muted-foreground">Show on invoice</span>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Company name</Label>
            <Input value={b.company_name} onChange={(e) => setB({ ...b, company_name: e.target.value })} placeholder="Acme Inc." />
          </div>
          <div>
            <Label>VAT / Tax number</Label>
            <Input value={b.vat_number} onChange={(e) => setB({ ...b, vat_number: e.target.value })} placeholder="GB123456789" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={b.phone} onChange={(e) => setB({ ...b, phone: e.target.value })} placeholder="+1 555 0100" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={b.email} onChange={(e) => setB({ ...b, email: e.target.value })} placeholder="billing@acme.com" />
          </div>
          <div className="sm:col-span-2">
            <Label>Address</Label>
            <Textarea rows={2} value={b.address} onChange={(e) => setB({ ...b, address: e.target.value })} placeholder="221B Baker Street, London NW1 6XE, UK" />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={b.website} onChange={(e) => setB({ ...b, website: e.target.value })} placeholder="acme.com" />
          </div>
          <div>
            <Label>Accent color</Label>
            <div className="flex items-center gap-2">
              <Input
                type="color"
                className="h-10 w-14 cursor-pointer p-1"
                value={b.accent_color}
                onChange={(e) => setB({ ...b, accent_color: e.target.value })}
              />
              <Input
                value={b.accent_color}
                onChange={(e) => setB({ ...b, accent_color: e.target.value })}
                className="font-mono"
                maxLength={7}
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>Footer note</Label>
            <Textarea rows={2} value={b.footer_note} onChange={(e) => setB({ ...b, footer_note: e.target.value })} placeholder="Thank you for your business." />
          </div>
        </div>

        <MediaPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={(_f, url) => {
            setB({ ...b, logo_url: url });
            setPickerOpen(false);
          }}
          accept="image/*"
        />
      </div>

      {/* Live preview */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Eye className="h-3.5 w-3.5" /> Live invoice preview
        </div>
        <InvoicePreview b={b} />
      </div>
    </div>
  );
}

function InvoicePreview({ b }: { b: Branding }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-white text-slate-900 shadow-sm">
      <div className="h-2" style={{ backgroundColor: b.accent_color }} />
      <div className="space-y-6 p-8">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-3">
            {b.show_logo && b.logo_url ? (
              <img src={b.logo_url} alt="Logo" className="h-14 w-14 object-contain" />
            ) : null}
            <div>
              <div className="text-lg font-bold">{b.company_name || "Your company name"}</div>
              <div className="mt-1 space-y-0.5 text-xs text-slate-600">
                {b.address && <div className="whitespace-pre-line">{b.address}</div>}
                {b.phone && <div>Tel: {b.phone}</div>}
                {b.email && <div>{b.email}</div>}
                {b.website && <div>{b.website}</div>}
                {b.vat_number && <div>VAT: {b.vat_number}</div>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold tracking-tight" style={{ color: b.accent_color }}>
              INVOICE
            </div>
            <div className="mt-1 font-mono text-xs text-slate-500">INV-2026-00001</div>
            <div className="mt-2 text-xs text-slate-500">Issue date</div>
            <div className="text-sm font-medium">{new Date().toLocaleDateString()}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 border-t border-slate-200 pt-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Bill to</div>
            <div className="mt-1 text-sm font-medium">Sample Customer Ltd.</div>
            <div className="text-xs text-slate-600">customer@example.com</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Amount due</div>
            <div className="text-2xl font-bold" style={{ color: b.accent_color }}>$1,250.00</div>
          </div>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="py-2">Description</th>
              <th className="py-2 text-right">Qty</th>
              <th className="py-2 text-right">Price</th>
              <th className="py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100">
              <td className="py-2">Professional services</td>
              <td className="py-2 text-right">10</td>
              <td className="py-2 text-right">$100.00</td>
              <td className="py-2 text-right">$1,000.00</td>
            </tr>
            <tr>
              <td className="py-2">Software license</td>
              <td className="py-2 text-right">1</td>
              <td className="py-2 text-right">$200.00</td>
              <td className="py-2 text-right">$200.00</td>
            </tr>
          </tbody>
        </table>

        <div className="ml-auto w-56 space-y-1 text-sm">
          <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>$1,200.00</span></div>
          <div className="flex justify-between text-slate-600"><span>Tax</span><span>$50.00</span></div>
          <div
            className="flex justify-between border-t border-slate-200 pt-1 font-semibold"
            style={{ color: b.accent_color }}
          >
            <span>Total</span><span>$1,250.00</span>
          </div>
        </div>

        {b.footer_note && (
          <div className="border-t border-slate-200 pt-4 text-center text-xs text-slate-500">
            {b.footer_note}
          </div>
        )}
      </div>
    </div>
  );
}
